import { useMemo, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useDiscoverablePartnerProperties } from "../use-discoverable-partner-properties";
import { useCreatePartnerFieldMap } from "../use-partner-field-map-mutations";

export function AddPartnerFieldDialog() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const discovery = useDiscoverablePartnerProperties(open);
  const create = useCreatePartnerFieldMap();

  const filtered = useMemo(() => {
    const props = discovery.data?.properties ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return props;
    return props.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q),
    );
  }, [discovery.data, query]);

  function toggle(name: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function submit() {
    const names = Array.from(picked);
    if (names.length === 0) return;
    let ok = 0;
    let fail = 0;
    for (const name of names) {
      try {
        await create.mutateAsync(
          { hubspot_property_name: name },
          { onError: () => {} }, // suppress hook's onError; we surface summary toast below
        );
        ok++;
      } catch {
        fail++;
      }
    }
    if (ok > 0) toast.success(`Added ${ok} field${ok === 1 ? "" : "s"}.`);
    if (fail > 0) toast.error(`${fail} field${fail === 1 ? "" : "s"} failed to add.`);
    setPicked(new Set());
    setQuery("");
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add HubSpot field
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add HubSpot partner fields</DialogTitle>
          <DialogDescription>
            Pick HubSpot properties to surface on the admin partner detail page.
            Core fields and already-mapped fields are hidden.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by label, name, or description…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 pl-8"
          />
        </div>

        {discovery.isLoading ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading from HubSpot…
          </div>
        ) : discovery.isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Failed to load: {discovery.error?.message ?? "unknown error"}
            <Button
              size="sm"
              variant="ghost"
              className="ml-2"
              onClick={() => discovery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
            {query
              ? "No properties match your search."
              : "All partner properties are already mapped."}
          </div>
        ) : (
          <div className="h-72 overflow-y-auto rounded-md border">
            <ul className="divide-y">
              {filtered.map((p) => (
                <li
                  key={p.name}
                  className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-muted/30"
                  onClick={() => toggle(p.name)}
                >
                  <Checkbox
                    checked={picked.has(p.name)}
                    onCheckedChange={() => toggle(p.name)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{p.label}</span>
                      <code className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
                        {p.name}
                      </code>
                      <Badge variant="secondary" className="text-[10px]">
                        {p.type}
                      </Badge>
                    </div>
                    {p.description && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {p.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={picked.size === 0 || create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Add{" "}
            {picked.size > 0
              ? `${picked.size} field${picked.size === 1 ? "" : "s"}`
              : "fields"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
